import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Image as ImageIcon, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function GalleryAdminPage() {
  const { user } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const fileRef = useRef(null);

  const fetchGallery = () => {
    api.get("/gallery").then(r => { setGallery(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchGallery(); }, []);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please select an image"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      formData.append("description", description);
      await api.post("/gallery", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded to gallery!");
      setDialogOpen(false);
      setTitle("");
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      fetchGallery();
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="gallery-admin-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Media</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Gallery Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">Upload photos that appear on the public landing page gallery section.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="upload-gallery-btn">
                <Plus size={16} weight="bold" /> Upload Photo
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Upload to Gallery</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm">Photo</Label>
                  <Input type="file" accept="image/*" ref={fileRef} className="mt-1 rounded-none" data-testid="gallery-file-input" />
                </div>
                <div>
                  <Label className="text-sm">Title</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1 rounded-none" placeholder="Photo title" data-testid="gallery-title-input" />
                </div>
                <div>
                  <Label className="text-sm">Description (optional)</Label>
                  <Input value={description} onChange={e => setDescription(e.target.value)} className="mt-1 rounded-none" placeholder="Brief description" />
                </div>
                <Button onClick={handleUpload} disabled={uploading} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="confirm-upload-btn">
                  {uploading ? "Uploading..." : "Upload Photo"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="aspect-square bg-slate-100 animate-pulse" />)}
          </div>
        ) : gallery.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">No gallery photos yet</p>
            <p className="text-sm text-slate-400">Upload photos to display on the public landing page.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((item, idx) => (
              <Card key={item.id} className="border border-slate-200 rounded-none shadow-sm overflow-hidden group" data-testid={`gallery-admin-item-${idx}`}>
                <div className="aspect-square overflow-hidden bg-slate-100">
                  <img src={item.image_url || item.image_path} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <CardContent className="p-3">
                  <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                  <p className="text-xs text-slate-500">{new Date(item.created_at).toLocaleDateString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
